<script setup lang="ts">
import { computed } from 'vue';
import { Article } from '~/types';
import { Link } from '@inertiajs/vue3';
import { resolveArticleComponent } from '~/utils/article';
import { useLoadArticles } from '~/composables/useLoadArticles';
import LoadingCard from '~/components/LoadingCard.vue';
import Container from '~/components/Container.vue';
import { ref } from 'vue';
import { A11y } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Swiper as SwiperClass, SwiperModule } from 'swiper/types';
import { Icon } from '@iconify/vue';

interface Props {
  anchor?: string;
  categories?: number[];
  limit?: number;
  title?: string;
  link_label?: string;
  link_url?: string;
}

const props = defineProps<Props>();

const params = computed<Article.RequestParameters>(() => {
  return {
    filter: {
      categories: props.categories || [],
    },
    per_page: props.limit ? props.limit : 8,
    sort: ['-published_at'],
  };
});

const { articles, loadData, loading } = useLoadArticles(params);

const modules: SwiperModule[] = [A11y];
const swiperUse = ref<SwiperClass | null>(null);

function onSwiper(swiper: SwiperClass) {
  swiperUse.value = swiper;
}

const swiperOptions = {
  init: false,
  wrapperTag: 'div',
  modules,
  slidesPerView: 'auto',
  keyboard: {
    enabled: true,
    onlyInViewport: false,
  },
};

loadData();
</script>
<template>
  <section :id="anchor" class="latest-news">
    <Container class="latest-news__container">
      <div class="latest-news__heading">
        <h2 class="latest-news__title">{{ title || 'Latest News' }}</h2>
        <div class="latest-news__buttons">
          <div class="latest-news__slider-nav">
            <button
              class="swiper-button-prev"
              :class="{
                'swiper-button-disabled': swiperUse?.isBeginning,
                'swiper-button-lock': swiperUse?.isLocked,
              }"
              @click="swiperUse?.slidePrev()"
            >
              <span class="sr-only">Prev Slide</span>
              <Icon icon="bi:arrow-left" />
            </button>
            <button
              class="swiper-button-next"
              :class="{
                'swiper-button-disabled': swiperUse?.isEnd,
                'swiper-button-lock': swiperUse?.isLocked,
              }"
              @click="swiperUse?.slideNext()"
            >
              <span class="sr-only">Next Slide</span>
              <Icon icon="bi:arrow-right" />
            </button>
          </div>
          <Link v-if="link_url" :href="link_url" class="latest-news__link">
            {{ link_label || 'View All' }}
            <span class="latest-news__icon-wrapper">
              <Icon icon="tabler:chevrons-right" class="latest-news__icon" />
            </span>
          </Link>
        </div>
      </div>
      <div v-if="articles && articles.length > 0" class="latest-news__slider-wrapper">
        <Swiper v-bind="swiperOptions" @swiper="onSwiper">
          <SwiperSlide v-for="(article, index) in articles" :key="`latest-news-item-${index}`">
            <Component :is="resolveArticleComponent(article)" :article="article" />
          </SwiperSlide>
        </Swiper>
      </div>
      <strong v-else-if="!loading" class="latest-news-list__title">No articles to display</strong>
      <div v-if="loading">
        <LoadingCard />
      </div>
    </Container>
  </section>
</template>

<style lang="scss" scoped>
.latest-news {
  @apply my-16 overflow-hidden;
  @apply md:my-20;
  @apply xl:my-24;

  &__container {
    @apply flex flex-col gap-6;
    @apply md:gap-10;
    @apply xl:gap-16;
  }

  &__heading {
    @apply w-full flex items-center gap-5 justify-between;
  }

  &__buttons {
    @apply flex items-center;
  }

  &__title {
    @apply font-semibold leading-[1.1] text-[24px];
    @apply 2xl:text-[30px];
    @apply 3xl:text-[38px];
  }

  &__slider-nav {
    @apply flex items-center;
  }

  &__icon-wrapper {
    @apply flex items-center justify-center rounded-full w-4 h-4 border border-accent;
  }

  &__icon {
    @apply w-full h-full;
  }

  &__link {
    @apply flex gap-2 whitespace-nowrap w-fit font-semibold items-center text-[14px] text-accent hover:text-dark [&>span]:border-accent ml-5;
    @apply xl:text-[16px];
  }

  .swiper {
    &-button-prev,
    &-button-next {
      @apply static text-accent after:hidden translate-y-0 m-0 h-auto w-auto p-2 [&>svg]:w-6 duration-300 hover:text-dark;
    }

    &-button-prev {
      @apply ml-5;
    }
  }

  :deep(.swiper) {
    @apply w-[calc(100%+1.5rem)] -ml-3 overflow-visible;
  }

  :deep(.swiper-slide) {
    @apply w-4/5 px-3;
    @apply md:w-1/2;
    @apply lg:w-1/3;
    @apply 2xl:w-[calc(100%/3.5)];
  }

  :deep(+ .campaign-progress) {
    @apply pt-20;
    @apply before:absolute before:bg-white before:w-full before:h-5 before:rounded-10 before:top-0 before:left-0 before:z-[2] before:-translate-y-1/2;
    @apply xl:before:rounded-30 xl:before:h-12;
    @apply xl:pt-32;
  }
}
</style>
